import {
  addLayer,
  duplicateLayer,
  getState,
  makeImageLayer,
  makeShapeLayer,
  makeTextLayer,
  makeVideoLayer,
  removeLayer,
  setAudio,
  setSelectedId,
  updateLayer,
  updateProject,
} from '../store/project';
import { extractVideoId } from '../audio/youtube';
import type {
  Background,
  FileAudioTrack,
  Layer,
  Ratio,
  ShapeKind,
  YoutubeAudioTrack,
} from '../types';

// JSON Schema in a form that BOTH Anthropic (input_schema) and OpenAI
// (function.parameters) accept directly.
export interface ToolDef {
  name: string;
  description: string;
  input_schema: Record<string, unknown>;
}

export const tools: ToolDef[] = [
  {
    name: 'get_project',
    description:
      'Read the current project state: canvas (ratio, safeRatio, background, duration) and the ordered list of layers with their ids, kinds, transforms, timing, and animation. Call this before suggesting concrete edits.',
    input_schema: { type: 'object', properties: {}, required: [] },
  },
  {
    name: 'add_text_layer',
    description:
      'Add a new text layer. Coordinates are CANVAS COORDS with center anchor (x/y is the layer center, not top-left). Default 9:16 canvas is 1080×1920 — center is 540,960. Times in ms.',
    input_schema: {
      type: 'object',
      properties: {
        text: { type: 'string' },
        x: { type: 'number', description: 'center X' },
        y: { type: 'number', description: 'center Y' },
        w: { type: 'number' },
        h: { type: 'number' },
        fontSize: { type: 'number', description: 'px; 60-180 for headlines' },
        fontWeight: { type: 'number', description: '100-900' },
        color: { type: 'string', description: 'hex like #ff5a5f' },
        align: { type: 'string', enum: ['left', 'center', 'right'] },
        start: { type: 'number', description: 'ms from clip start' },
        end: { type: 'number', description: 'ms from clip start' },
        enter_type: {
          type: 'string',
          enum: ['fade', 'slide', 'pop', 'typing', 'strike', 'none'],
        },
        enter_duration: { type: 'number' },
        enter_from: { type: 'string', enum: ['left', 'right', 'top', 'bottom'] },
        exit_type: { type: 'string', enum: ['fade', 'slide', 'none'] },
        exit_duration: { type: 'number' },
        exit_to: { type: 'string', enum: ['left', 'right', 'top', 'bottom'] },
        name: { type: 'string', description: 'optional layer name shown in panel' },
      },
      required: ['text'],
    },
  },
  {
    name: 'add_shape_layer',
    description:
      'Add a shape layer. Supported shapes: box, circle, triangle, star, diamond, arrow, heart, pentagon, hexagon, line. For "line" only stroke is rendered (uses fill as stroke color, strokeWidth ≥ 2).',
    input_schema: {
      type: 'object',
      properties: {
        shape: {
          type: 'string',
          enum: [
            'box', 'circle', 'triangle', 'star', 'diamond', 'arrow', 'heart', 'pentagon', 'hexagon', 'line',
          ],
        },
        x: { type: 'number' },
        y: { type: 'number' },
        w: { type: 'number' },
        h: { type: 'number' },
        fill: { type: 'string' },
        stroke: { type: 'string' },
        strokeWidth: { type: 'number' },
        start: { type: 'number' },
        end: { type: 'number' },
        name: { type: 'string' },
      },
      required: ['shape'],
    },
  },
  {
    name: 'add_image_layer',
    description:
      'Add an image layer from a URL or data URL (jpg/png/webp/gif/svg). Useful when you have an https:// link or a small inline image.',
    input_schema: {
      type: 'object',
      properties: {
        src: { type: 'string', description: 'https:// URL or data:image/...;base64,...' },
        x: { type: 'number' }, y: { type: 'number' },
        w: { type: 'number' }, h: { type: 'number' },
        fit: { type: 'string', enum: ['cover', 'contain'] },
        rotate: { type: 'number' }, opacity: { type: 'number' },
        start: { type: 'number' }, end: { type: 'number' },
        enter_type: { type: 'string', enum: ['fade', 'slide', 'pop', 'none'] },
        enter_duration: { type: 'number' },
        exit_type: { type: 'string', enum: ['fade', 'slide', 'none'] },
        exit_duration: { type: 'number' },
        name: { type: 'string' },
      },
      required: ['src'],
    },
  },
  {
    name: 'add_video_layer',
    description: 'Add a video layer from a URL or data URL (mp4/webm/mov/m4v).',
    input_schema: {
      type: 'object',
      properties: {
        src: { type: 'string' },
        x: { type: 'number' }, y: { type: 'number' },
        w: { type: 'number' }, h: { type: 'number' },
        fit: { type: 'string', enum: ['cover', 'contain'] },
        muted: { type: 'boolean' },
        loop: { type: 'boolean' },
        rotate: { type: 'number' }, opacity: { type: 'number' },
        start: { type: 'number' }, end: { type: 'number' },
        enter_type: { type: 'string', enum: ['fade', 'slide', 'pop', 'none'] },
        enter_duration: { type: 'number' },
        exit_type: { type: 'string', enum: ['fade', 'slide', 'none'] },
        exit_duration: { type: 'number' },
        name: { type: 'string' },
      },
      required: ['src'],
    },
  },
  {
    name: 'update_layer',
    description:
      'Update fields of an existing layer by id. Pass only the fields you want to change. For nested transform fields, pass them as patch.transform = { x, y, w, h, rotate, opacity }.',
    input_schema: {
      type: 'object',
      properties: {
        id: { type: 'string' },
        patch: {
          type: 'object',
          description:
            'Partial fields to merge. Recognised: name, visible, start, end, transform, text, fontSize, fontWeight, color, align, letterSpacing, lineHeight, src, fit, fill, stroke, strokeWidth, enter, exit',
        },
      },
      required: ['id', 'patch'],
    },
  },
  {
    name: 'delete_layer',
    description: 'Delete a layer by id.',
    input_schema: {
      type: 'object',
      properties: { id: { type: 'string' } },
      required: ['id'],
    },
  },
  {
    name: 'duplicate_layer',
    description: 'Duplicate a layer by id; the copy keeps the same timing.',
    input_schema: {
      type: 'object',
      properties: { id: { type: 'string' } },
      required: ['id'],
    },
  },
  {
    name: 'select_layer',
    description: 'Select a layer in the editor (highlights it).',
    input_schema: {
      type: 'object',
      properties: { id: { type: 'string' } },
      required: ['id'],
    },
  },
  {
    name: 'set_background',
    description: 'Change the canvas background. Pass kind plus the relevant fields.',
    input_schema: {
      type: 'object',
      properties: {
        kind: { type: 'string', enum: ['color', 'gradient', 'image', 'video'] },
        color: { type: 'string' },
        from: { type: 'string' },
        to: { type: 'string' },
        angle: { type: 'number' },
        src: { type: 'string', description: 'http(s) URL or data URL' },
        fit: { type: 'string', enum: ['cover', 'contain'] },
        loop: { type: 'boolean' },
        muted: { type: 'boolean' },
      },
      required: ['kind'],
    },
  },
  {
    name: 'set_canvas',
    description: 'Change canvas ratio, safe-zone ratio, or total duration.',
    input_schema: {
      type: 'object',
      properties: {
        ratio: { type: 'string', enum: ['9:16', '1:1', '4:5', '16:9'] },
        safeRatio: { type: 'string', enum: ['9:16', '1:1', '4:5', '16:9', 'none'] },
        duration: { type: 'number', description: 'ms' },
      },
    },
  },
  {
    name: 'set_audio_youtube',
    description:
      'Set the project background music to a YouTube video. Provide URL (or 11-char videoId) plus the song range (ms within the song). Volume 0..1, optional fade in/out (ms). The song plays in real time and starts at the given offset when project playhead is 0.',
    input_schema: {
      type: 'object',
      properties: {
        url: { type: 'string', description: 'YouTube URL or 11-char video ID' },
        songStart: { type: 'number', description: 'ms within the song to begin' },
        songEnd: { type: 'number', description: 'ms within the song to stop' },
        volume: { type: 'number', description: '0..1, default 0.6' },
        fadeIn: { type: 'number', description: 'ms (volume fade-in at project start)' },
        fadeOut: { type: 'number', description: 'ms (volume fade-out before project end)' },
        label: { type: 'string', description: 'Optional song title' },
      },
      required: ['url'],
    },
  },
  {
    name: 'set_audio_file',
    description:
      'Set background music to an audio file URL or data URL (mp3/m4a/wav/ogg/flac/aac). Useful when the user uploads or pastes an audio link. Same volume/fade/range options as YouTube.',
    input_schema: {
      type: 'object',
      properties: {
        src: { type: 'string', description: 'audio URL or data:audio/...;base64,... URL' },
        name: { type: 'string' },
        songStart: { type: 'number' },
        songEnd: { type: 'number' },
        volume: { type: 'number' },
        fadeIn: { type: 'number' },
        fadeOut: { type: 'number' },
        label: { type: 'string' },
      },
      required: ['src'],
    },
  },
  {
    name: 'clear_audio',
    description: 'Remove background music from the project.',
    input_schema: { type: 'object', properties: {} },
  },
];

export interface ToolResultOk {
  ok: true;
  result: unknown;
}
export interface ToolResultErr {
  ok: false;
  error: string;
}
export type ToolResult = ToolResultOk | ToolResultErr;

export function executeTool(name: string, rawArgs: unknown): ToolResult {
  const args = (rawArgs ?? {}) as Record<string, unknown>;
  try {
    switch (name) {
      case 'get_project':
        return ok(stripProjectForLLM());
      case 'add_text_layer':
        return ok(applyAddText(args));
      case 'add_shape_layer':
        return ok(applyAddShape(args));
      case 'add_image_layer':
        return ok(applyAddImage(args));
      case 'add_video_layer':
        return ok(applyAddVideo(args));
      case 'update_layer':
        return ok(applyUpdate(args));
      case 'delete_layer':
        removeLayer(String(args.id));
        return ok('deleted');
      case 'duplicate_layer':
        duplicateLayer(String(args.id));
        return ok('duplicated');
      case 'select_layer':
        setSelectedId(String(args.id));
        return ok('selected');
      case 'set_background':
        return ok(applySetBackground(args));
      case 'set_canvas':
        return ok(applySetCanvas(args));
      case 'set_audio_youtube':
        return ok(applySetAudioYoutube(args));
      case 'set_audio_file':
        return ok(applySetAudioFile(args));
      case 'clear_audio':
        setAudio(undefined);
        return ok('audio cleared');
      default:
        return err(`unknown tool: ${name}`);
    }
  } catch (e) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

// ─── helpers ───
function ok(result: unknown): ToolResultOk {
  return { ok: true, result };
}
function err(message: string): ToolResultErr {
  return { ok: false, error: message };
}

function stripProjectForLLM(): unknown {
  // Strip data URLs from background and media layers — they bloat context.
  const project = getState().project;
  return {
    ...project,
    canvas: {
      ...project.canvas,
      background: stripBgSrc(project.canvas.background),
    },
    layers: project.layers.map((l) => stripLayerSrc(l)),
  };
}
function stripBgSrc(bg: Background): Background {
  if (bg.kind === 'image' || bg.kind === 'video') {
    return { ...bg, src: bg.src && bg.src.startsWith('data:') ? '<<dataUrl omitted>>' : bg.src };
  }
  return bg;
}
function stripLayerSrc(l: Layer): Layer {
  if (l.kind === 'image' || l.kind === 'video') {
    return { ...l, src: l.src && l.src.startsWith('data:') ? '<<dataUrl omitted>>' : l.src };
  }
  return l;
}

function applyAddText(args: Record<string, unknown>): { id: string } {
  const layer = makeTextLayer(String(args.text ?? '새 텍스트')) as Extract<Layer, { kind: 'text' }>;
  if (typeof args.name === 'string') layer.name = args.name;
  if (typeof args.x === 'number') layer.transform.x = args.x;
  if (typeof args.y === 'number') layer.transform.y = args.y;
  if (typeof args.w === 'number') layer.transform.w = args.w;
  if (typeof args.h === 'number') layer.transform.h = args.h;
  if (typeof args.fontSize === 'number') layer.fontSize = args.fontSize;
  if (typeof args.fontWeight === 'number') layer.fontWeight = args.fontWeight;
  if (typeof args.color === 'string') layer.color = args.color;
  if (args.align === 'left' || args.align === 'center' || args.align === 'right') layer.align = args.align;
  if (typeof args.start === 'number') layer.start = args.start;
  if (typeof args.end === 'number') layer.end = args.end;
  applyEnterExit(layer, args);
  addLayer(layer);
  return { id: layer.id };
}

const SHAPE_KINDS: ShapeKind[] = [
  'box', 'circle', 'triangle', 'star', 'diamond', 'arrow', 'heart', 'pentagon', 'hexagon', 'line',
];

function applyAddShape(args: Record<string, unknown>): { id: string } {
  const layer = makeShapeLayer() as Extract<Layer, { kind: 'shape' }>;
  if (typeof args.shape === 'string' && SHAPE_KINDS.includes(args.shape as ShapeKind)) {
    layer.shape = args.shape as ShapeKind;
  }
  if (typeof args.name === 'string') layer.name = args.name;
  if (typeof args.x === 'number') layer.transform.x = args.x;
  if (typeof args.y === 'number') layer.transform.y = args.y;
  if (typeof args.w === 'number') layer.transform.w = args.w;
  if (typeof args.h === 'number') layer.transform.h = args.h;
  if (typeof args.fill === 'string') layer.fill = args.fill;
  if (typeof args.stroke === 'string') layer.stroke = args.stroke;
  if (typeof args.strokeWidth === 'number') layer.strokeWidth = args.strokeWidth;
  if (typeof args.start === 'number') layer.start = args.start;
  if (typeof args.end === 'number') layer.end = args.end;
  addLayer(layer);
  return { id: layer.id };
}

function applyAddImage(args: Record<string, unknown>): { id: string } {
  const src = String(args.src ?? '');
  if (!src) throw new Error('src required');
  const layer = makeImageLayer(src, typeof args.name === 'string' ? args.name : '이미지') as Extract<Layer, { kind: 'image' }>;
  if (typeof args.x === 'number') layer.transform.x = args.x;
  if (typeof args.y === 'number') layer.transform.y = args.y;
  if (typeof args.w === 'number') layer.transform.w = args.w;
  if (typeof args.h === 'number') layer.transform.h = args.h;
  if (typeof args.rotate === 'number') layer.transform.rotate = args.rotate;
  if (typeof args.opacity === 'number') layer.transform.opacity = clamp01(args.opacity);
  if (args.fit === 'cover' || args.fit === 'contain') layer.fit = args.fit;
  if (typeof args.start === 'number') layer.start = args.start;
  if (typeof args.end === 'number') layer.end = args.end;
  applyEnterExit(layer, args);
  addLayer(layer);
  return { id: layer.id };
}

function applyAddVideo(args: Record<string, unknown>): { id: string } {
  const src = String(args.src ?? '');
  if (!src) throw new Error('src required');
  const layer = makeVideoLayer(src, typeof args.name === 'string' ? args.name : '동영상') as Extract<Layer, { kind: 'video' }>;
  if (typeof args.x === 'number') layer.transform.x = args.x;
  if (typeof args.y === 'number') layer.transform.y = args.y;
  if (typeof args.w === 'number') layer.transform.w = args.w;
  if (typeof args.h === 'number') layer.transform.h = args.h;
  if (typeof args.rotate === 'number') layer.transform.rotate = args.rotate;
  if (typeof args.opacity === 'number') layer.transform.opacity = clamp01(args.opacity);
  if (args.fit === 'cover' || args.fit === 'contain') layer.fit = args.fit;
  if (typeof args.muted === 'boolean') layer.muted = args.muted;
  if (typeof args.loop === 'boolean') layer.loop = args.loop;
  if (typeof args.start === 'number') layer.start = args.start;
  if (typeof args.end === 'number') layer.end = args.end;
  applyEnterExit(layer, args);
  addLayer(layer);
  return { id: layer.id };
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function applyEnterExit(layer: Layer, args: Record<string, unknown>): void {
  if (typeof args.enter_type === 'string' && args.enter_type !== 'none') {
    const dir = ['left', 'right', 'top', 'bottom'].includes(String(args.enter_from))
      ? (args.enter_from as 'left' | 'right' | 'top' | 'bottom')
      : undefined;
    layer.enter = {
      type: args.enter_type as 'fade' | 'slide' | 'pop' | 'typing' | 'strike',
      duration: typeof args.enter_duration === 'number' ? args.enter_duration : 400,
      ...(dir ? { from: dir } : {}),
    };
  }
  if (typeof args.exit_type === 'string' && args.exit_type !== 'none') {
    const dir = ['left', 'right', 'top', 'bottom'].includes(String(args.exit_to))
      ? (args.exit_to as 'left' | 'right' | 'top' | 'bottom')
      : undefined;
    layer.exit = {
      type: args.exit_type as 'fade' | 'slide',
      duration: typeof args.exit_duration === 'number' ? args.exit_duration : 400,
      ...(dir ? { to: dir } : {}),
    };
  }
}

function applyUpdate(args: Record<string, unknown>): string {
  const id = String(args.id);
  const patch = (args.patch ?? {}) as Partial<Layer>;
  updateLayer(id, patch);
  return 'updated';
}

function applySetBackground(args: Record<string, unknown>): string {
  const kind = String(args.kind);
  let bg: Background;
  if (kind === 'color') {
    bg = { kind: 'color', color: String(args.color ?? '#0e0e10') };
  } else if (kind === 'gradient') {
    bg = {
      kind: 'gradient',
      from: String(args.from ?? '#fff8ee'),
      to: String(args.to ?? '#ffe1c4'),
      angle: typeof args.angle === 'number' ? args.angle : 180,
    };
  } else if (kind === 'image') {
    bg = {
      kind: 'image',
      src: String(args.src ?? ''),
      fit: args.fit === 'contain' ? 'contain' : 'cover',
    };
  } else {
    bg = {
      kind: 'video',
      src: String(args.src ?? ''),
      fit: args.fit === 'contain' ? 'contain' : 'cover',
      loop: args.loop !== false,
      muted: args.muted !== false,
    };
  }
  updateProject((p) => ({ ...p, canvas: { ...p.canvas, background: bg } }));
  return 'background updated';
}

function applySetAudioYoutube(args: Record<string, unknown>): string {
  const url = String(args.url ?? '');
  const videoId = extractVideoId(url);
  if (!videoId) throw new Error(`Could not extract YouTube videoId from: ${url}`);
  const songStart = typeof args.songStart === 'number' ? args.songStart : 0;
  const songEnd = typeof args.songEnd === 'number' ? args.songEnd : songStart + 30000;
  if (songEnd <= songStart) throw new Error('songEnd must be greater than songStart');
  const track: YoutubeAudioTrack = {
    id: `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    kind: 'youtube',
    videoId,
    songStart,
    songEnd,
    volume: typeof args.volume === 'number' ? Math.max(0, Math.min(1, args.volume)) : 0.6,
    ...(typeof args.fadeIn === 'number' && args.fadeIn > 0 ? { fadeIn: args.fadeIn } : {}),
    ...(typeof args.fadeOut === 'number' && args.fadeOut > 0 ? { fadeOut: args.fadeOut } : {}),
    ...(typeof args.label === 'string' && args.label.trim() ? { label: args.label.trim() } : {}),
  };
  setAudio(track);
  return `audio set (youtube): ${videoId} ${songStart}-${songEnd}ms`;
}

function applySetAudioFile(args: Record<string, unknown>): string {
  const src = String(args.src ?? '');
  if (!src) throw new Error('src required');
  const songStart = typeof args.songStart === 'number' ? args.songStart : 0;
  const songEnd = typeof args.songEnd === 'number' ? args.songEnd : songStart + 30000;
  if (songEnd <= songStart) throw new Error('songEnd must be greater than songStart');
  const track: FileAudioTrack = {
    id: `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    kind: 'file',
    src,
    songStart,
    songEnd,
    volume: typeof args.volume === 'number' ? Math.max(0, Math.min(1, args.volume)) : 0.6,
    ...(typeof args.fadeIn === 'number' && args.fadeIn > 0 ? { fadeIn: args.fadeIn } : {}),
    ...(typeof args.fadeOut === 'number' && args.fadeOut > 0 ? { fadeOut: args.fadeOut } : {}),
    ...(typeof args.label === 'string' && args.label.trim() ? { label: args.label.trim() } : {}),
    ...(typeof args.name === 'string' && args.name.trim() ? { name: args.name.trim() } : {}),
  };
  setAudio(track);
  return `audio set (file): ${songStart}-${songEnd}ms`;
}

function applySetCanvas(args: Record<string, unknown>): string {
  updateProject((p) => {
    const next = { ...p, canvas: { ...p.canvas } };
    if (typeof args.ratio === 'string') next.canvas.ratio = args.ratio as Ratio;
    if (typeof args.safeRatio === 'string') {
      next.canvas.safeRatio = args.safeRatio === 'none' ? null : (args.safeRatio as Ratio);
    }
    if (typeof args.duration === 'number') next.duration = args.duration;
    return next;
  });
  return 'canvas updated';
}
